import { useContext, useState } from 'react';
import { AttachMoney } from '@mui/icons-material';
import { Button, Typography, Alert } from '@mui/material';
import { ClickAwayListener } from '@mui/material';
import { DatePicker } from 'formik-mui-lab';
import { FormControl, FormControlLabel, FormLabel, InputAdornment, Radio, RadioGroup, Box } from '@mui/material';
import { Formik, Form, Field } from 'formik';
import { LocalizationProvider } from '@mui/lab';
import { TextField } from 'formik-mui';
import * as Yup from 'yup';
import AdapterDayjs from '@mui/lab/AdapterDayjs';
import dayjs from 'helpers/dayjs';
// Out Components
import { AuthContext } from 'context/auth-context';
import { CardContext } from 'context/cards/cardContext';
import CategoryInput from '../CreateCard/CategoryInput';
import CustomLinearProgress from './LinearProgress';
// Utils and Helpers
import { hasCreditLine, hasCreditModel } from 'helpers/businesses';
import { getMaxLimitAmount } from 'helpers/cards';
import { isAdminUser } from 'helpers/auth';
import { numberFormat } from 'helpers/i18n';
import { REPLENISH_FRECUENCY } from 'helpers/constants';

const isValidLimitAmount = (amount, business, statistics) => {
    let limitAmount = null;
    let fundingAmount = null;
    if (hasCreditModel(business) && hasCreditLine(business)) {
        limitAmount = amount;
        return Number(limitAmount) <= statistics.creditAvailable;
    }
    fundingAmount = amount;
    return Number(fundingAmount) <= statistics.availableToAddtoCards;
};

const EditCardForm = ({ onSubmit, setDidFormValuesChange }) => {
    const { card, statistics } = useContext(CardContext);
    const { businessInfo, userInfo } = useContext(AuthContext);
    const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);
    const [hasClickEditLimit, setHasClickedEditLimit] = useState(false);

    const validationSchema = Yup.object().shape({
        autoLock: Yup.date()
            .typeError('Invalid date')
            .nullable()
            .test('autoLock', 'Invalid date', autoLock => {
                if (!autoLock) return true;
                if (dayjs(autoLock).isSame(card?.autoLock)) return true;
                if (dayjs(autoLock).isAfter(dayjs())) return true;
                return false;
            })
            .optional(),
        label: Yup.string().trim().max(100, 'Max 100 characters').required('Required'),
        memo: Yup.string().trim().max(255, 'Max 255 characters').optional(),
        limitAmount: Yup.number()
            .typeError('INVALID AMOUNT')
            .positive('Invalid amount')
            .min(1, 'It must be higher than $1.00')
            .required('Required')
            .test(
                'isValidIncreaseLimitAmount',
                <span style={{ color: 'red', textTransform: 'none' }}>
                    Limit should be less than or equal to {numberFormat(getMaxLimitAmount(businessInfo, statistics), 2, 2)}
                </span>,
                limitAmount => isValidLimitAmount(limitAmount, businessInfo, statistics)
            )
    });

    const statusWidth = hasClickEditLimit ? '100%' : '';

    const getEstimatedNewCardLimit = () => {
        if (hasCreditModel(businessInfo) && hasCreditLine(businessInfo)) {
            return numberFormat(getMaxLimitAmount(businessInfo, statistics), 2, 2);
        }
        return numberFormat(Number(card.balance) + statistics.availableToAddtoCards, 2, 2);
    };

    return (
        <Formik initialValues={card} enableReinitialize={true} validationSchema={validationSchema} onSubmit={onSubmit}>
            {({ dirty, values }) => (
                <Form id="edit-card-form">
                    {setDidFormValuesChange(dirty)}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box className="card-status-box" style={{ width: statusWidth }}>
                            <Box display="flex" justifyContent="space-between">
                                <FormLabel className="card-input-label">
                                    {hasClickEditLimit ? 'Set New Card Limit' : 'Current Card Limit'}
                                </FormLabel>
                                <Box>
                                    <FormLabel className="card-limit-label" sx={{ fontSize: '12px', color: '#182C55' }}>
                                        {hasClickEditLimit ? 'You can set a limit up to ' : ''}
                                    </FormLabel>
                                    <FormLabel
                                        className="card-limit-number"
                                        sx={{ fontSize: '12px', fontWeight: 'bold', color: '#1BABE2' }}>
                                        {hasClickEditLimit ? getEstimatedNewCardLimit() : ''}
                                    </FormLabel>
                                </Box>
                            </Box>
                            {hasClickEditLimit ? (
                                <Box display="flex">
                                    <FormControl fullWidth>
                                        <Field
                                            component={TextField}
                                            variant="outlined"
                                            name="limitAmount"
                                            inputProps={{
                                                style: {
                                                    fontWeight: 'bold',
                                                    fontSize: '20px',
                                                    color: 'var(--main-color)'
                                                }
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment
                                                        position="start"
                                                        sx={{ color: 'var(--main-color)', fontWeight: 'bold' }}>
                                                        <AttachMoney
                                                            sx={{ color: values?.id ? '#909DA6' : 'black', fontSize: '30px' }}
                                                        />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </FormControl>
                                </Box>
                            ) : (
                                <Box className="edit-card__card-balance">{numberFormat(values.limitAmount || 0, 2, 2)}</Box>
                            )}
                        </Box>
                        <Box className="card-button-box" alignItems="flex-end" marginTop="22px">
                            {!hasClickEditLimit &&
                            isAdminUser(userInfo?.role?.id) &&
                            businessInfo?.settings?.isCardLimitUpdateEnabled ? (
                                <Button variant="text" onClick={() => setHasClickedEditLimit(true)}>
                                    Edit Limit
                                </Button>
                            ) : null}
                        </Box>
                    </Box>
                    <Box sx={{ marginBottom: '15px' }}>
                        <Box component="label" sx={{ color: 'var(--main-color)', fontWeight: 'bold', fontSize: '12px' }}>
                            {hasClickEditLimit ? (
                                <Box>
                                    <Alert
                                        severity="info"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            backgroundColor: '#F7F8F9',
                                            fontSize: '12px',
                                            marginTop: '5px'
                                        }}>
                                        Card available balance will be set to the new card limit
                                    </Alert>
                                </Box>
                            ) : (
                                <Box component="label" sx={{ color: 'var(--main-color)', fontWeight: 'normal' }}>
                                    <CustomLinearProgress progressValue={values} />
                                    <Box display="flex">
                                        <Typography
                                            sx={{
                                                color: 'var(--main-color)',
                                                fontWeight: 'bold',
                                                fontSize: '12px',
                                                marginRight: '5px'
                                            }}>
                                            {numberFormat(values.balance || 0, 2, 2)}
                                        </Typography>{' '}
                                        {!hasClickEditLimit && ' available to spend'}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>
                    {values.requested_limit_amount && (
                        <Alert severity="info" sx={{ marginBottom: '24px' }}>
                            A new card limit of {numberFormat(values.requested_limit_amount || 0, 2, 2)} is being requested
                        </Alert>
                    )}
                    <FormControl fullWidth sx={{ marginBottom: '24px' }}>
                        <FormLabel className="card-input-label">Cardholder Name</FormLabel>
                        <Field component={TextField} variant="outlined" name="name" type="text" size="small" disabled />
                    </FormControl>
                    <FormControl fullWidth sx={{ marginBottom: '24px' }}>
                        <FormLabel className="card-input-label">Replenishment Type</FormLabel>
                        <RadioGroup row aria-labelledby="-row-radio-buttons-group-label" name="disabled-row-radio-buttons-group">
                            <FormControlLabel
                                checked={card?.has_manual_replenish === REPLENISH_FRECUENCY.ONE_TIME}
                                value="disabled"
                                disabled
                                control={<Radio />}
                                label="Manual"
                            />
                            <FormControlLabel
                                checked={card?.has_manual_replenish === REPLENISH_FRECUENCY.DAILY}
                                value="disabled"
                                disabled
                                control={<Radio />}
                                label="Automatic"
                            />
                        </RadioGroup>
                    </FormControl>
                    <ClickAwayListener onClickAway={() => setIsDatepickerOpen(false)}>
                        <FormControl fullWidth sx={{ marginBottom: '24px' }}>
                            <FormLabel className="card-input-label">Auto Lock Date</FormLabel>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Field
                                    component={DatePicker}
                                    variant="outlined"
                                    name="autoLock"
                                    mask="__/__/____"
                                    minDate={dayjs().add(1, 'day')}
                                    closeOnSelect
                                    open={isDatepickerOpen}
                                    onAccept={() => setIsDatepickerOpen(false)}
                                    textField={{
                                        size: 'small',
                                        onClick: () => setIsDatepickerOpen(isDatepickerOpen => !isDatepickerOpen)
                                    }}
                                />
                            </LocalizationProvider>
                        </FormControl>
                    </ClickAwayListener>
                    <FormControl fullWidth sx={{ marginBottom: '24px' }}>
                        <FormLabel required className="card-input-label">
                            Choose a category
                        </FormLabel>
                        <CategoryInput variant="outlined" name="label" size="small" />
                    </FormControl>
                    <FormControl fullWidth>
                        <FormLabel className="card-input-label">Add a Memo (Optional)</FormLabel>
                        <Field
                            component={TextField}
                            name="memo"
                            multiline
                            rows={4}
                            size="small"
                            placeholder="Add additional information about this card"
                        />
                    </FormControl>
                </Form>
            )}
        </Formik>
    );
};

export default EditCardForm;
